import type { ChartPaint, Paints } from "./paints.ts"

/** One line of a chart's legend and one layer of its marks: what it is, and the paint it takes. */
export type ChartSeries<K extends string> = { key: K; label: string; paint: ChartPaint }

/**
 * One UTC day (`YYYY-MM-DD`) and each series' value on it. `values` is null on a day whose history
 * isn't known, which a chart and its table both say apart from a day of nothing.
 */
export type ChartDay<K extends string> = { day: string; values: Readonly<Record<K, number>> | null }

/**
 * A note pinned to a day: a short label drawn on the chart, such as a change's number. Several
 * may share a day; that day's mark names them all.
 */
export type ChartAnnotation = { day: string; label: string }

export type DailyChartInput<K extends string> = {
  series: readonly ChartSeries<K>[]
  /** Every day of the range, in order, including those of nothing and those not known. */
  days: readonly ChartDay<K>[]
  /** What the values count, as the value axis is titled: "Tickets". */
  unit: string
  /** The medium's colours: the web app's tokens, or a PDF's sRGB. */
  paints: Paints
  /** How a value is written on the axis, in a tooltip and in the table: a whole count unless set. */
  formatValue?: (value: number) => string
  /**
   * The value axis's ticks from zero, the last its top, each written by `format`: for a unit whose
   * ticks must share one precision, as money's do. A nice scale to the largest value unless set.
   */
  valueTicks?: { values: readonly number[]; format: (value: number) => string }
  annotations?: readonly ChartAnnotation[]
  /** What the annotations are, as an annotated day's tooltip names them: "Notes" unless set. */
  annotationsName?: string
  /** The annotations' paint, `muted-foreground` unless the chart's meaning gives them one. */
  annotationPaint?: ChartPaint
  /**
   * The plot's width in px, given where marks too close to stand apart are to stand as one (a
   * phone's chart). Without it, only a day's own annotations share a mark.
   */
  plotWidth?: number
  /**
   * The plot's width in px as laid out, which decides how far apart the day axis's labels stand:
   * every day, every other day, each Monday, every other Monday or each month's first. 640 unless
   * set.
   */
  axisWidth?: number
  /**
   * The chart's own words, its axes' ticks and titles and a span's "Not known": their size in px
   * and how strongly they're inked. TanStack's 11px, faded to sit back on a screen, unless set; a
   * printed page sets them at its captions' size and full strength, as its other quiet words are.
   */
  text?: { size: number; opacity: number }
  /** The day picked, which stands in a band, or none. */
  picked?: string | null
  /** Called with the day a click, tap, Enter or Space picks, or null when a click picks none. */
  onPick?: (day: string | null) => void
}

const count = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 })

/** The input's way of writing a value, or a count's. */
export function formatOf<K extends string>({ formatValue }: DailyChartInput<K>) {
  return formatValue ?? ((value: number) => count.format(value))
}
